/**
 * simulateContactFlow.ts - Contact flow simulation for editable canvas
 *
 * Purpose:
 *   Simulates contact progression through automation flows for preview/testing
 *   in the editable canvas. Unlike the ReadOnlyCanvas which uses API data,
 *   this provides a visual simulation without backend execution data.
 *
 * Usage:
 *   import { enrichNodesWithSimulatedContacts, enrichEdgesWithSimulatedContacts } from './simulateContactFlow';
 *
 * Simulation Approach:
 *   - Distributes contacts across all nodes in the flow
 *   - Places contacts on edges based on flow structure
 *   - Provides visual preview of contact flow without execution data
 *
 * Related:
 *   - contactFlowMapping.ts (API-driven approach for ReadOnlyCanvas)
 *   - index.tsx (editable canvas)
 */

import { type Node, type Edge } from '@xyflow/react';
import { type FlowContact } from '../types/contacts';

interface NodeWithContactData extends Record<string, any> {
	contactColors?: string[];
	contactCount?: number;
}

interface EdgeWithContactData extends Record<string, any> {
	activeContacts?: FlowContact[];
}

/**
 * Enriches nodes with simulated contact distribution.
 * Shows contacts on all queued/action nodes in the flow for preview purposes.
 *
 * @param nodes - Array of automation flow nodes
 * @param activeContacts - Array of active contacts to distribute
 * @returns Array of nodes enriched with contact colors and counts
 */
export function enrichNodesWithSimulatedContacts<T extends NodeWithContactData>(
	nodes: Node<T>[],
	activeContacts: FlowContact[]
): Node<T>[] {
	if (activeContacts.length === 0) {
		return nodes;
	}

	return nodes.map(node => {
		// Show contacts on all step nodes (not trigger)
		if (node.id === 'trigger') {
			return node;
		}

		// For simulation, show all active contacts on every step node
		const contactColors = activeContacts.map(contact => contact.color);

		return {
			...node,
			data: {
				...node.data,
				contactColors,
				contactCount: activeContacts.length,
			},
		};
	});
}

/**
 * Enriches edges with simulated contact animation.
 * Distributes contacts across all edges to show flow progression.
 *
 * For a more realistic simulation, contacts are distributed based on edge position:
 * - Early edges (from trigger) get all contacts
 * - Later edges get progressively fewer contacts (simulation of flow progression)
 *
 * @param nodes - Array of automation flow nodes (used to determine edge depth)
 * @param edges - Array of automation flow edges
 * @param activeContacts - Array of active contacts to animate
 * @returns Array of edges enriched with active contacts
 */
export function enrichEdgesWithSimulatedContacts<T extends EdgeWithContactData>(
	nodes: Node[],
	edges: Edge<T>[],
	activeContacts: FlowContact[]
): Edge<T>[] {
	if (activeContacts.length === 0) {
		return edges.map(edge => ({
			...edge,
			data: {
				...edge.data,
				activeContacts: [],
			},
		}));
	}

	// Build a map of node depths (distance from trigger)
	const nodeDepths = new Map<string, number>();
	nodeDepths.set('trigger', 0);

	// BFS to calculate depth of each node
	const queue: { id: string; depth: number }[] = [{ id: 'trigger', depth: 0 }];
	const visited = new Set<string>(['trigger']);

	while (queue.length > 0) {
		const current = queue.shift()!;

		// Find all edges from this node
		edges.forEach(edge => {
			if (edge.source === current.id && !visited.has(edge.target)) {
				visited.add(edge.target);
				nodeDepths.set(edge.target, current.depth + 1);
				queue.push({ id: edge.target, depth: current.depth + 1 });
			}
		});
	}

	// Calculate max depth for normalization
	const _maxDepth = Math.max(...Array.from(nodeDepths.values()));

	// Enrich edges based on their position in the flow
	const enrichedEdges = edges.map(edge => {
		const sourceDepth = nodeDepths.get(edge.source) ?? 0;
		const targetDepth = nodeDepths.get(edge.target) ?? 0;
		const _edgeDepth = Math.max(sourceDepth, targetDepth);

		// For simulation, show all contacts on all edges
		// In a more sophisticated simulation, you could reduce contacts on deeper edges
		// to simulate flow progression and filtering
		const contactsOnEdge = activeContacts;

		// Optional: Simulate contact reduction through flow
		// Uncomment to show fewer contacts on deeper edges:
		/*
		if (maxDepth > 0) {
			const retentionRate = 1 - (edgeDepth / (maxDepth + 1)) * 0.5; // Keep 50-100% of contacts
			const contactCount = Math.max(1, Math.ceil(activeContacts.length * retentionRate));
			contactsOnEdge = activeContacts.slice(0, contactCount);
		}
		*/

		return {
			...edge,
			data: {
				...edge.data,
				activeContacts: contactsOnEdge,
			},
		};
	});

	return enrichedEdges;
}
